import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/Button';
import { PageWrapper } from '../../components/PageWrapper';
import { SearchTable } from '../../components/Table';
import clsx from 'clsx';
import { Add } from '@mui/icons-material';
import { UserDetail } from '../../components/UserDetail';
import {
  AllUserRes,
  UserRes,
  UserRole,
  CustomExcelUserRole,
  UserRoleExcelExclusive,
} from '../../services/api.types';
import { ROUTES } from '../../utils/constants';
import {
  translateUserRoles,
  translateUserRoleToExcelReq,
} from '../../utils/translate-enums';
import { Modal } from '../../components/Modal';
import { usersService } from '../../services/users.service';
import { Checkbox } from '../../components/Checkbox';
import { FormProvider, useForm } from 'react-hook-form';
import { apiService } from '../../services/api';
import { saveAs } from 'file-saver';
import { useAppSelector } from '../../hooks';
import SummarizeIcon from '@mui/icons-material/Summarize';

type User = {
  id: number;
  'Nome Completo': string;
  Email: string;
  Cargo: string;
};

export const UsersPage = () => {
  const [users, setUsers] = useState<UserRes[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserRes | undefined>();
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [excelExportRole, setExcelExportRole] = useState<
    CustomExcelUserRole | undefined
  >(undefined);

  const activeCheckboxForm = useForm<{ active: boolean }>({
    defaultValues: { active: true },
  });
  const active = activeCheckboxForm.watch('active');

  const user = useAppSelector((state) => state.user);

  const isAdminOrEmployee =
    user?.role === UserRole.ADMIN || user?.role === UserRole.EMPLOYEE;

  const usersTable = useMemo(() => {
    return users
      .filter((user) => active === user.active)
      .map((user) => ({
        id: user.id,
        'Nome Completo': user.fullName,
        Email: user.email ?? '-',
        Cargo: translateUserRoles(user.role, user.responsible?.isStudent),
        Ativo: user.active ? 'Sim' : 'Não',
      }));
  }, [users, active]);

  useEffect(() => {
    setIsFetching(true);
    usersService
      .getAllUsers({ includeDisabled: true })
      .then((response: AllUserRes) => {
        response.users.sort((a, b) =>
          a.displayName.localeCompare(b.displayName)
        );
        setUsers(response.users);
      })
      .finally(() => {
        setIsFetching(false);
      });
  }, []);

  const handleSelect = (user: User) => {
    const u = users.find((u) => user.id === u.id);
    setSelectedUser(u);
  };

  const handleSelectById = (userId: number) => {
    const u = users.find((u) => userId === u.id);
    setSelectedUser(u);
  };

  const handleActivate = async (updatedActive: boolean) => {
    if (!selectedUser) return;

    const updatedUser = { ...selectedUser, active: updatedActive };
    setUsers((users) => {
      const index = users.findIndex((u) => u.id === selectedUser.id);
      const updatedUsers = [...users];
      updatedUsers[index] = updatedUser;
      return updatedUsers;
    });
    setSelectedUser(updatedUser);
  };

  const handleClose = () => {
    setSelectedUser(undefined);
  };

  const downloadExcel = async () => {
    if (!isAdminOrEmployee) return;
    if (!excelExportRole) setExcelExportRole(UserRoleExcelExclusive.ALL);
    const wantsActiveUsers = activeCheckboxForm.getValues('active');
    const excelBuffer = await apiService.getUsersExcel(
      excelExportRole,
      wantsActiveUsers
    );
    const dataExcel = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const filename = !excelExportRole ? 'users' : `users_${excelExportRole}`;
    const date = new Date().toISOString().split('T')[0];
    saveAs(dataExcel, `${filename.toLowerCase()}_${date}.xlsx`);
  };

  return (
    <PageWrapper>
      <div
        className={clsx(
          'flex flex-col-reverse justify-end lg:flex-row',
          'items-end lg:items-start lg:justify-between',
          'h-full gap-4'
        )}
      >
        <SearchTable
          rows={usersTable}
          getSummary={(row) => row['Nome Completo']}
          isFetching={isFetching}
          filterColumn="Cargo"
          filterLabel="Filtrar por"
          searchPlaceholder="Pesquise aqui"
          isSelectable
          onSelectRow={handleSelect}
          TranslateEnum={(k) => translateUserRoles(k as UserRole)}
          AboveHeader={
            <div className="flex justify-between">
              <Button
                disabled={!isAdminOrEmployee}
                startIcon={<SummarizeIcon />}
                circular
                onClick={downloadExcel}
                className="w-min whitespace-nowrap"
              >
                Exportar para Excel
              </Button>
              <FormProvider {...activeCheckboxForm}>
                <Checkbox name="active" label="Usuários ativos?" />
              </FormProvider>
            </div>
          }
          onFilterChange={async (s: string) => {
            const translatedStringToRole = translateUserRoleToExcelReq(s);
            if (!isAdminOrEmployee) return;
            setExcelExportRole(translatedStringToRole);
          }}
        />
        <div className="w-full max-w-[320px] flex flex-col gap-4">
          <Button circular startIcon={<Add />} href={ROUTES.users.create}>
            Criar novo usuário
          </Button>

          {selectedUser && (
            <>
              <div className="hidden lg:block">
                <UserDetail
                  user={selectedUser}
                  onActivate={handleActivate}
                  onClose={handleClose}
                  onSelectUser={(userId) => {
                    if (!userId) return;
                    handleSelectById(userId);
                  }}
                />
              </div>
              <Modal
                className="block lg:hidden"
                open={!!selectedUser}
                onClose={handleClose}
                transparentBackground
                disableScrollLock
              >
                <UserDetail
                  user={selectedUser}
                  onActivate={handleActivate}
                  onClose={handleClose}
                  onSelectUser={(userId) => {
                    if (!userId) return;
                    handleSelectById(userId);
                  }}
                />
              </Modal>
            </>
          )}
        </div>
      </div>
    </PageWrapper>
  );
};
